import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { MdCheck, MdClose } from 'react-icons/md';

const STEP_DELAY_MS = 450;

/**
 * Shown when "Set Live" is clicked — walks the flow (entrance -> ... -> exit) and shows exactly
 * what was checked at each step, pass/fail, before the automation is actually allowed to go live.
 *
 * The backend returns the full `checks` array in one shot, but we reveal it to the user one item
 * at a time (spinner -> tick/cross), n8n-style, so it reads like the flow is being checked live
 * instead of dumping a flat pass/fail list.
 */
const AutomationSetLiveModal = ({ show, checking, result, submitting, onClose, onConfirm, onFocusNode }) => {
    const checks = Array.isArray(result?.checks) ? result.checks : [];
    const ok = Boolean(result?.ok);

    const [revealedCount, setRevealedCount] = useState(0);
    const timerRef = useRef(null);

    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        if (checking || !show) {
            setRevealedCount(0);
            return;
        }

        if (!checks.length) {
            setRevealedCount(0);
            return;
        }

        setRevealedCount(0);
        let i = 0;
        const revealNext = () => {
            i += 1;
            setRevealedCount(i);
            if (i < checks.length) {
                timerRef.current = setTimeout(revealNext, STEP_DELAY_MS);
            }
        };
        timerRef.current = setTimeout(revealNext, STEP_DELAY_MS);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checking, show, result]);

    const allRevealed = !checking && checks.length > 0 && revealedCount >= checks.length;
    const showSummary = allRevealed;

    return (
        <Modal show={show} onHide={submitting ? undefined : onClose} centered className="ns-modal automation-setlive-modal">
            <Modal.Header closeButton={!submitting}>
                <Modal.Title>Pre-launch checks</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {checking ? (
                    <div className="automation-setlive-checking">Checking your flow…</div>
                ) : (
                    <>
                        <ul className="automation-setlive-checklist">
                            {checks.map((c, i) => {
                                const revealed = i < revealedCount;
                                const stateClass = !revealed ? 'is-pending' : (c.ok ? 'is-ok' : 'is-fail');
                                return (
                                    <li
                                        key={i}
                                        className={`${stateClass}${revealed && !c.ok && c.nodeId ? ' is-clickable' : ''}`}
                                        onClick={revealed && !c.ok && c.nodeId && onFocusNode ? () => onFocusNode(c.nodeId) : undefined}
                                        title={revealed && !c.ok && c.nodeId ? 'Click to jump to this step' : undefined}
                                    >
                                        <span className="automation-setlive-checklist-icon">
                                            {!revealed ? (
                                                <span className="automation-setlive-spinner" />
                                            ) : c.ok ? (
                                                <MdCheck size={16} />
                                            ) : (
                                                <MdClose size={16} />
                                            )}
                                        </span>
                                        <span className="automation-setlive-checklist-label">
                                            {c.label}
                                            {revealed && !c.ok && c.detail ? (
                                                <span className="automation-setlive-checklist-detail">{c.detail}</span>
                                            ) : null}
                                            {revealed && !c.ok && c.nodeId ? (
                                                <span className="automation-setlive-checklist-goto">Go to this step &rarr;</span>
                                            ) : null}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                        {showSummary ? (
                            <div className={`automation-setlive-summary ${ok ? 'is-ok' : 'is-fail'}`}>
                                {ok
                                    ? 'All checks passed — this automation is ready to go live.'
                                    : 'Fix the highlighted steps before this automation can go live - click a failed row to jump to it.'}
                            </div>
                        ) : null}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn_theme_secondary" onClick={onClose} disabled={submitting}>
                    {showSummary && ok ? 'Cancel' : 'Close'}
                </button>
                {showSummary && ok ? (
                    <button type="button" className="add_app_btn btn_theme_primary" onClick={onConfirm} disabled={submitting}>
                        <span>{submitting ? 'Going live…' : 'Go Live'}</span>
                    </button>
                ) : null}
            </Modal.Footer>
        </Modal>
    );
};

export default AutomationSetLiveModal;
